/**
 * Every fan-visible string on the team follow control and its alert editor,
 * plus the pure state decisions that pick between them.
 *
 * Source: `.agents/design-prompts/16-team-follow-control.md`'s `## Copy`
 * section. Three strings here are planner-authored because no source
 * document covers the state:
 *   - `ALERTS_OFF_LABEL` ("Alerts off") for a followed team with both alerts
 *     switched off -- the brief has no string for it.
 *   - `FOLLOW_FAILED_TEXT` is reused for an unfollow failure; the brief
 *     designs no unfollow-failure state.
 *   - The editor titles carry `<team name>` with no article ("You're
 *     following Ravens.", not "the Ravens.") -- supplying "the" would be
 *     grammar this client has to guess per team.
 *
 * `TEAM_ALERTS_ROW_TITLE` ("Alerts") is the full follow control's alerts
 * row title, from `.agents/views/16-team-follow-control.html`'s `.alertlink
 * .al .t`.
 */

export type typeAlertKindKey = "gameStart" | "gameEnd";

export type typeTeamFollowViewState =
  "notFollowing" | "following" | "pending" | "failed";

export type typeAlertSwitchRow = {
  alertKindKey: typeAlertKindKey;
  label: string;
  isEnabled: boolean;
};

export const ALERT_ORDER: readonly typeAlertKindKey[] = [
  "gameStart",
  "gameEnd",
];

export const ALERT_LABELS: Record<typeAlertKindKey, string> = {
  gameStart: "Game start",
  gameEnd: "Game end",
};

export const TEAM_ALERTS_ROW_TITLE = "Alerts";
export const FOLLOW_TO_GET_ALERTS_LABEL = "Follow to get alerts";
export const ALERTS_OFF_LABEL = "Alerts off";
export const BOTH_ALERTS_LABEL = "Game start, game end";
export const FOLLOW_FAILED_TEXT = "Couldn't follow. Try again.";
export const EDITOR_FIRST_TIME_BODY =
  "We'll send you game start and game end alerts. Turn on more below, or edit anytime from this team's card.";
export const EDITOR_DONE_LABEL = "Done";
export const SIGN_IN_GATE_TITLE = "Sign in to follow teams.";
export const SIGN_IN_GATE_BODY =
  "Following a team keeps its games in your Scores feed and sends you its alerts.";
export const SIGN_IN_GATE_PRIMARY = "Sign in";
export const SIGN_IN_GATE_SECONDARY = "Not now";

/**
 * `isPending` wins over everything else, so a fan retrying mid-flight never
 * sees a flash of the failed state before the spinner returns.
 */
export function teamFollowViewState(input: {
  isFollowing: boolean;
  isPending: boolean;
  errorCode: string | null;
}): typeTeamFollowViewState {
  if (input.isPending) {
    return "pending";
  }
  if (input.errorCode !== null) {
    return "failed";
  }
  return input.isFollowing ? "following" : "notFollowing";
}

export function isSignedOutErrorCode(errorCode: string | null): boolean {
  return errorCode === "UNAUTHENTICATED";
}

/**
 * Reorders to `ALERT_ORDER` regardless of the server's order, so `Game
 * start` is always above `Game end`. Drops any entry whose key is not in
 * `ALERT_ORDER`.
 */
export function buildAlertSwitchRows(
  alerts: readonly { alertKindKey: typeAlertKindKey; isEnabled: boolean }[],
): typeAlertSwitchRow[] {
  const byKey = new Map(
    alerts.map((alert) => [alert.alertKindKey, alert.isEnabled]),
  );
  const rows: typeAlertSwitchRow[] = [];
  for (const key of ALERT_ORDER) {
    const isEnabled = byKey.get(key);
    if (isEnabled !== undefined) {
      rows.push({ alertKindKey: key, label: ALERT_LABELS[key], isEnabled });
    }
  }
  return rows;
}

export function alertsSummaryLabel(input: {
  isFollowing: boolean;
  alerts: readonly { alertKindKey: typeAlertKindKey; isEnabled: boolean }[];
}): string {
  if (!input.isFollowing) {
    return FOLLOW_TO_GET_ALERTS_LABEL;
  }
  const enabled = new Set(
    input.alerts
      .filter((alert) => alert.isEnabled)
      .map((alert) => alert.alertKindKey),
  );
  const gameStartOn = enabled.has("gameStart");
  const gameEndOn = enabled.has("gameEnd");
  if (gameStartOn && gameEndOn) {
    return BOTH_ALERTS_LABEL;
  }
  if (gameStartOn) {
    return ALERT_LABELS.gameStart;
  }
  if (gameEndOn) {
    return ALERT_LABELS.gameEnd;
  }
  return ALERTS_OFF_LABEL;
}

export function editorTitle(teamName: string, isFirstFollow: boolean): string {
  return isFirstFollow
    ? `You're following ${teamName}.`
    : `Alerts for ${teamName}.`;
}

export function editorBody(isFirstFollow: boolean): string | null {
  return isFirstFollow ? EDITOR_FIRST_TIME_BODY : null;
}
